import React, { type ReactNode } from "react";
import Image from "next/legacy/image";
import { Flex, Text, Box, Button, Circle } from "@chakra-ui/react";
import { Link } from "ui/link";
import { useIntercom } from "hooks/use-intercom";
import { trackEvent } from "lib/analytics";
import { Badge } from "ui/badge";
import { cardHoverTranslation } from "utils";

export type IntegrationRecord = {
  type: "destination" | "source";
  name: string;
  slug: string;
  status: IntegrationStatus;
  categories: Array<{
    name: string;
  }>;
  icon?: {
    asset?: {
      url?: string;
    };
  };
};

type IntegrationStatus = "released" | "coming_soon" | "shadow" | "alpha" | "deprecated";

type IntegrationProps = {
  hit: IntegrationRecord;
  onSendEvent: (eventType: string, hit: IntegrationRecord, eventName?: string | undefined) => void;
};

export function IntegrationCardSkeleton({ hit }: Pick<IntegrationProps, "hit">) {
  return (
    <Box borderRadius="base" p="small" bg="brand.backgroundSecondary.light">
      <Circle size="36px" bg="new.gray.150" />
      <Box
        w={`${hit.name.length * 8}px`}
        maxW="90%"
        minW="50px"
        h="smallest"
        mt="small"
        mb="tinier"
        bg="new.gray.150"
        borderRadius="base"
      />
    </Box>
  );
}

export function Integration({ hit, onSendEvent }: IntegrationProps) {
  const { newChat } = useIntercom();

  // Released integrations are shown as a link that navigates to the integration page
  if (hit?.type && hit?.slug && hit?.name && hit?.status === "released") {
    return (
      /**
       * Parent element is necessary to avoid a layout shift when hovering over the bottom of the card.
       * See `cardHoverTranslation` for more details.
       */
      <Box className="group" boxSize="100%">
        <Link
          href={`integrations/${hit.type}s/${hit.slug}`}
          variant="unstyled"
          boxSize="100%"
          aria-label={`Learn more about the ${hit.name} destination.`}
          borderRadius="base"
          _groupHover={cardHoverTranslation}
          onClick={() => {
            onSendEvent("conversion", hit, `Site Catalog Integration Clicked`);
            trackEvent("Integration Clicked");
          }}
        >
          <IntegrationCard hit={hit} />
        </Link>
      </Box>
    );
  }

  // @TODO send analytics events? mirror those here
  // https://www.notion.so/hightouch/Sources-Destinations-Catalog-3b6636cc52744240a9085063a98d2572#9413a69fa79b4c54a5b40cd63eb4c2ab
  const openChatWithMessage = (hit: IntegrationRecord) => {
    if (hit.status === "shadow") {
      newChat(
        `Hi, I would like to request a new ${hit.type}. I want to sync data ${hit.type === "source" ? "from" : "to"} ${
          hit.name
        } in order to help the _____ team accomplish _____.`,
      );
    } else if (hit.status !== "released" && hit.type === "source") {
      //
      newChat(
        `Hi, I would like to request early access to the ${hit.name} source. My use case involves syncing _____ from ${hit.name} to _____. Please contact me when it’s ready.`,
      );
    } else if (hit.status !== "released" && hit.type === "destination") {
      newChat(
        `Hi, I would like to request early access to the ${hit.name} destination. My use case involves syncing _____ to ${hit.name}. Please contact me when it’s ready.`,
      );
    }
  };

  // Non-released integrations are shown as buttons that will launch Intercom with a custom message
  return (
    <Button
      variant="unstyled"
      boxSize="100%"
      borderRadius="base"
      className="group"
      border="none"
      onClick={() => {
        onSendEvent("click", hit, `Site Catalog Integration Clicked`);
        openChatWithMessage(hit);
        trackEvent("Integration Clicked");
      }}
    >
      <IntegrationCard hit={hit} />
    </Button>
  );
}

function IntegrationCardChip({ color, children }: { color?: string; children: ReactNode }) {
  return (
    <Badge bg={`new.${color ?? "gray"}.50`} color={`new.${color ?? "gray"}.900`}>
      {children}
    </Badge>
  );
}

/**
 * Use this styled card for the integrations within a separate link or button element.
 * This will keep the box shadow visible even when the element is in focus (since the Chakra-default
 * box shadow will get applied to the focused element - keep that the parent element).
 */
function IntegrationCard({ hit }: Pick<IntegrationProps, "hit">) {
  let popoverText = null;
  const chips = [];

  if (hit.type === "source") {
    chips.push(<IntegrationCardChip>Source</IntegrationCardChip>);
  }

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (hit.status) {
    case "alpha": {
      popoverText = `This ${hit.type} is in private preview. Contact us for access!`;
      chips.push(<IntegrationCardChip color="green">Private preview</IntegrationCardChip>);
      break;
    }

    case "coming_soon": {
      popoverText = `This ${hit.type} is coming soon. Contact us to join the waitlist!`;
      chips.push(<IntegrationCardChip color="green">Coming soon</IntegrationCardChip>);
      break;
    }

    case "shadow": {
      popoverText = `We don't support this ${hit.type} yet. Contact us to request it!`;
      chips.push(<IntegrationCardChip>Request</IntegrationCardChip>);
      break;
    }

    // No default
  }

  return (
    <Flex
      aria-label={`${hit.name} ${hit.type}.`}
      position="relative"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="flex-start"
      boxSize="100%"
      p="small"
      bg={hit.status === "shadow" ? "brand.backgroundSecondary.light" : "brand.background.light"}
      borderRadius="base"
      boxShadow="card"
      border="1px"
      borderColor="brand.border.extraLight"
    >
      {popoverText && (
        <Box
          position="absolute"
          top="0px"
          left="0px"
          bottom="0px"
          right="0px"
          bg="rgba(255, 255, 255, 0.8)"
          backdropFilter="blur(8px)"
          zIndex={100}
          opacity={0}
          transition="opacity .15s ease-in-out"
          borderRadius="base"
          _groupHover={{ opacity: 1 }}
          _groupFocusVisible={{ opacity: 1 }}
        >
          <Box position="absolute" w="80%" top="50%" left="50%" transform="translate(-50%, -50%)" textStyle="caption1">
            {popoverText}
          </Box>
        </Box>
      )}

      <Box position="absolute" right={3} top={4}>
        <Flex gap={1}>{chips}</Flex>
      </Box>

      <Flex direction="column" align="flex-start" width="100%">
        <Box width="36px" height="36px" pos="relative" fontSize="xs">
          {hit.icon?.asset?.url && (
            <Image src={`${hit.icon.asset.url}?w=72`} alt={`${hit.name}.`} layout="fill" objectFit="contain" />
          )}
        </Box>

        <Flex width="100%" alignItems="center" justifyContent="space-between" mt="smaller">
          <Text textStyle="e4" fontSize="md" noOfLines={1} wordBreak="break-all">
            {hit.name}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
